import React, { useMemo } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useGetOrderDeadlineText } from '../../hooks/useGetOrderDeadlineText';
import { Colors, Flex, IconClose, InputBox, Typography } from '@banhala/ui-kit';
import { isNil, isNotNil, localizeNumber } from 'shared/src/utils';
import { IconBrandIconToday } from '../../../IconBrandIconToday';
import { CounterStepper } from './CounterStepper';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
const MAX_TEXT_OPTION_LENGTH = 255;

export const PricingListItem: React.FC<{
  option: GoodsOption;
  onUpdateOption: (option: GoodsOption) => void;
  onPressDelete: () => void;
}> = props => {
  const { option } = props;
  const isGiftMode = useIsBottomSheetGiftMode();

  const isTodayDelivery = useMemo(() => {
    if (isGiftMode) return false;

    return option.delivery_type === 'today' || option.delivery_type === 'shak';
  }, [option.delivery_type, isGiftMode]);

  const { data: goodsOptionBrief } = useGoodsOptionsBrief();

  const getDeadlineText = useGetOrderDeadlineText();

  const orderDeadline = useMemo(() => {
    if (!isTodayDelivery) return null;

    return getDeadlineText({ delivery_type: option.delivery_type });
  }, [isTodayDelivery, getDeadlineText, option.delivery_type]);

  return (
    <Flex
      padding={{ horizontal: 16, vertical: 16 }}
      borderRadius={8}
      borderWidth={1}
      borderColor={'border_secondary'}
      gap={12}
    >
      <Flex gap={8}>
        <Flex.Row alignItems={'center'} justifyContent={'space-between'}>
          <Flex flex={1}>
            <Typography type={'body3'} numberOfLines={3}>
              {option.option_names.join(' / ')}
            </Typography>
          </Flex>
          <Flex margin={{ left: 12 }} onPress={props.onPressDelete}>
            <IconClose size={18} fill={Colors.content_disabled} />
          </Flex>
        </Flex.Row>
        {isNotNil(goodsOptionBrief) &&
          isNotNil(goodsOptionBrief.text_option) && (
            <Flex gap={8}>
              <Typography type={'subtitle4'} color={'content_primary'}>
                {goodsOptionBrief.text_option.title}
              </Typography>
              <InputBox
                type={'outline'}
                variant={'body2'}
                maxLength={MAX_TEXT_OPTION_LENGTH}
                placeholder={goodsOptionBrief.text_option.placeholder}
                value={option.message ?? ''}
                onChangeText={text => {
                  props.onUpdateOption({
                    ...option,
                    message: text === '' ? undefined : text,
                  });
                }}
              ></InputBox>
            </Flex>
          )}
        {isNotNil(orderDeadline) && (
          <Flex.Row gap={4}>
            <Flex
              padding={{ horizontal: 6, vertical: 3 }}
              backgroundColor={'blue5'}
              borderRadius={10}
            >
              <IconBrandIconToday width={41} height={11} />
            </Flex>
            <Typography type={'body4'} color={'content_slate_primary'}>
              {orderDeadline}
            </Typography>
          </Flex.Row>
        )}
      </Flex>

      <Flex.Row
        height={40}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Flex>
          <CounterStepper
            max={
              isNil(props.option.max_order_limit_count)
                ? props.option.stock
                : Math.min(
                    props.option.stock,
                    props.option.max_order_limit_count,
                  )
            }
            min={1}
            value={props.option.purchase_count ?? 1}
            onChangeValue={nextVal => {
              props.onUpdateOption({
                ...props.option,
                purchase_count: nextVal,
              });
            }}
          />
        </Flex>
        <Flex alignItems={'flex-end'} gap={4}>
          <Typography type={'subtitle1'}>
            {localizeNumber(
              props.option.price * (props.option.purchase_count ?? 1),
            )}
            원
          </Typography>
          <Flex.Row gap={4}>
            {props.option.stock >= 1 && props.option.stock <= 9 && (
              <Typography type={'body4'} color={'content_pink_primary'}>
                {props.option.stock}개 남음
              </Typography>
            )}
            {isNotNil(props.option.max_order_limit_count) && (
              <Typography type={'body4'} color={'content_tertiary'}>
                (인당 최대 {localizeNumber(props.option.max_order_limit_count)}개
                구매가능)
              </Typography>
            )}
          </Flex.Row>
        </Flex>
      </Flex.Row>
    </Flex>
  );
};
